import { type ReactNode } from "react";
import type { FileHandlingProps } from "../atoms";
import { BASE_FILE_SIZE_LIMIT_BYTES, Dropzone, FileInput, Icon, Text } from "../atoms";

type FileDropzoneProps = FileHandlingProps & {
  onFilesChange?: (files: File[]) => void;
  browseButtonText?: string;
  className?: string;
  children?: ReactNode;
  files?: File[];
  showIcon?: boolean;
  supportedFileTypesText?: string;
  maxFileSizeText?: string;
  setIsDropActive?: (isDropActive: boolean) => void;
  hadError?: boolean;
  errorText?: string;
  disabled?: boolean;
  tryAgainText?: string;
  successText?: string;
};

const dropZoneInlineClasses = "text-base font-area font-extrabold";

function formatDropZoneText(children: string, browseButtonText?: string) {
  const firstSpaceIndex = children.indexOf(" ");
  const firstWord = firstSpaceIndex === -1 ? children : children.slice(0, firstSpaceIndex);
  const rest = firstSpaceIndex === -1 ? "" : children.slice(firstSpaceIndex);
  return (
    <>
      <span className={`${dropZoneInlineClasses} text-primary-100 inline`}>{firstWord}</span>
      <span className={`${dropZoneInlineClasses} text-gray-800/70 inline`}>{rest}</span>
      {browseButtonText ? (
        <>
          <span className={`${dropZoneInlineClasses} text-gray-800/70 inline`}> </span>
          <span className={`${dropZoneInlineClasses} text-primary-100 inline`}>{browseButtonText}</span>
        </>
      ) : null}
    </>
  );
}

function FileHints({
  supportedFileTypesText,
  maxFileSizeText,
}: Readonly<{
  supportedFileTypesText?: string;
  maxFileSizeText?: string;
}>) {
  if (!supportedFileTypesText && !maxFileSizeText) return null;
  const hintText = [supportedFileTypesText, maxFileSizeText].filter(Boolean).join(", ");
  return (
    <Text size="sm" color="text-gray-800/70">
      {hintText}
    </Text>
  );
}

function UploadedFileContent({
  files,
  successText,
  onFileRemoval,
}: Readonly<{
  files: File[];
  successText?: string;
  onFileRemoval: () => void;
}>) {
  return (
    <>
      {files.map((file: File) => (
        <div className="flex h-full w-full items-center gap-8" key={file.name}>
          <div className="flex shrink-0 flex-col items-center">
            <Icon type="uploadPrescription" sizeClasses="h-[70px] w-[70px]" />
          </div>
          <div className="flex w-full grow flex-col gap-2">
            <Text size="sm" color="text-primary-100" fontWeight="font-medium">
              {successText ?? ""}
            </Text>
            <div className="flex items-center gap-2">
              <Text size="base" color="text-gray-800" fontWeight="font-extrabold">
                {file.name}
              </Text>
              <button
                type="button"
                onClick={onFileRemoval}
                className="flex shrink-0 cursor-pointer items-center justify-center">
                <Icon type="moveToTrash" sizeClasses="h-6 w-6" className="text-gray-800" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

function ErrorStateContent({
  showIcon,
  errorText,
  tryAgainText,
  onTryAgain,
  supportedFileTypesText,
  maxFileSizeText,
  acceptedFileTypes,
  onFileTypeError,
  fileSizeLimitBytes,
  onFileSizeLimitExceeded,
  onAddFiles,
}: Readonly<{
  showIcon: boolean;
  errorText?: string;
  tryAgainText?: string;
  onTryAgain: () => void;
  supportedFileTypesText?: string;
  maxFileSizeText?: string;
} & Pick<
  FileHandlingProps,
  "acceptedFileTypes" | "onFileTypeError" | "fileSizeLimitBytes" | "onFileSizeLimitExceeded"
> & {
  onAddFiles: (files: File[]) => void;
}>) {
  return (
    <div className="flex h-full flex-row items-center gap-4">
      {showIcon ? (
        <div className="flex w-full shrink-0 flex-col items-center">
          <Icon type="prescriptionUploadFailed" sizeClasses="h-full w-full" />
        </div>
      ) : null}
      <div className="flex w-full grow flex-col gap-0.5">
        <Text size="base" color="text-error-500" fontWeight="font-extrabold">
          {errorText ?? ""}
        </Text>
        <button
          type="button"
          onClick={onTryAgain}
          className="w-fit cursor-pointer">
          <span className="text-base font-area font-extrabold text-primary-100">
            {tryAgainText ?? ""}
          </span>
        </button>
        <FileHints supportedFileTypesText={supportedFileTypesText} maxFileSizeText={maxFileSizeText} />
      </div>
      <FileInput
        fileInputId="dropzone-error-input"
        acceptedFileTypes={acceptedFileTypes}
        onFileTypeError={onFileTypeError}
        fileSizeLimitBytes={fileSizeLimitBytes}
        onFileSizeLimitExceeded={onFileSizeLimitExceeded}
        onAddFiles={onAddFiles}>
        <span className="hidden" />
      </FileInput>
    </div>
  );
}

function DisabledStateContent({
  showIcon,
  children,
  browseButtonText,
  supportedFileTypesText,
  maxFileSizeText,
}: Readonly<{
  showIcon: boolean;
  children?: ReactNode;
  browseButtonText?: string;
  supportedFileTypesText?: string;
  maxFileSizeText?: string;
}>) {
  return (
    <div className="flex h-full flex-row items-center justify-between opacity-30">
      {showIcon ? (
        <div className="flex w-full shrink-0 flex-col items-center">
          <Icon type="prescriptionUploadFailed" sizeClasses="h-full w-full" />
        </div>
      ) : null}
      <div className="flex w-full grow flex-col">
        <div className="flex flex-col gap-2">
          <div className="text-base font-extrabold leading-6 text-gray-800/70">
            {formatDropZoneText(children as string, browseButtonText)}
          </div>
          <FileHints supportedFileTypesText={supportedFileTypesText} maxFileSizeText={maxFileSizeText} />
        </div>
      </div>
    </div>
  );
}

function IdleStateContent({
  showIcon,
  children,
  browseButtonText,
  supportedFileTypesText,
  maxFileSizeText,
  acceptedFileTypes,
  onFileTypeError,
  fileSizeLimitBytes,
  onFileSizeLimitExceeded,
  onAddFiles,
}: Readonly<{
  showIcon: boolean;
  children?: ReactNode;
  browseButtonText: string;
  supportedFileTypesText?: string;
  maxFileSizeText?: string;
} & Pick<
  FileHandlingProps,
  "acceptedFileTypes" | "onFileTypeError" | "fileSizeLimitBytes" | "onFileSizeLimitExceeded"
> & {
  onAddFiles: (files: File[]) => void;
}>) {
  return (
    <div className="flex h-full flex-row items-center justify-between">
      {showIcon ? (
        <div className="mr-6 flex w-full flex-col items-center">
          <Icon type="upload" sizeClasses="h-full w-full" />
        </div>
      ) : null}
      <div className="flex w-full grow flex-col">
        <div className="flex flex-col gap-2">
          <div className="leading-6">
            {formatDropZoneText(children as string)}{" "}
            <FileInput
              fileInputId="dropzone"
              acceptedFileTypes={acceptedFileTypes}
              onFileTypeError={onFileTypeError}
              fileSizeLimitBytes={fileSizeLimitBytes}
              onFileSizeLimitExceeded={onFileSizeLimitExceeded}
              onAddFiles={onAddFiles}>
              <span className="text-base font-area font-extrabold text-primary-100 inline cursor-pointer">
                {browseButtonText}
              </span>
            </FileInput>
          </div>
          <FileHints supportedFileTypesText={supportedFileTypesText} maxFileSizeText={maxFileSizeText} />
        </div>
      </div>
    </div>
  );
}

export const FileDropZone = ({
  acceptedFileTypes,
  onFilesChange,
  onFileTypeError,
  fileSizeLimitBytes = BASE_FILE_SIZE_LIMIT_BYTES,
  onFileSizeLimitExceeded,
  children,
  browseButtonText = "",
  files = [],
  className = "",
  showIcon = true,
  supportedFileTypesText,
  maxFileSizeText,
  setIsDropActive,
  hadError = false,
  errorText,
  disabled = false,
  tryAgainText,
  successText,
}: FileDropzoneProps) => {
  const onDragStateChange = (dragActive: boolean) => (setIsDropActive ? setIsDropActive(dragActive) : null);

  const handleFileAddition = (newFiles: File[]) => {
    onFilesChange?.(newFiles);
  };

  const handleFileRemoval = () => {
    onFilesChange?.([]);
  };

  const handleTryAgain = () => {
    const fileInput = document.getElementById("dropzone-error-input") as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  const renderContent = () => {
    if (files.length > 0) {
      return <UploadedFileContent files={files} successText={successText} onFileRemoval={handleFileRemoval} />;
    }
    if (hadError) {
      return (
        <ErrorStateContent
          showIcon={showIcon}
          errorText={errorText}
          tryAgainText={tryAgainText}
          onTryAgain={handleTryAgain}
          supportedFileTypesText={supportedFileTypesText}
          maxFileSizeText={maxFileSizeText}
          acceptedFileTypes={acceptedFileTypes}
          onFileTypeError={onFileTypeError}
          fileSizeLimitBytes={fileSizeLimitBytes}
          onFileSizeLimitExceeded={onFileSizeLimitExceeded}
          onAddFiles={handleFileAddition}
        />
      );
    }
    if (disabled) {
      return (
        <DisabledStateContent
          showIcon={showIcon}
          browseButtonText={browseButtonText}
          supportedFileTypesText={supportedFileTypesText}
          maxFileSizeText={maxFileSizeText}>
          {children}
        </DisabledStateContent>
      );
    }
    return (
      <IdleStateContent
        showIcon={showIcon}
        browseButtonText={browseButtonText}
        supportedFileTypesText={supportedFileTypesText}
        maxFileSizeText={maxFileSizeText}
        acceptedFileTypes={acceptedFileTypes}
        onFileTypeError={onFileTypeError}
        fileSizeLimitBytes={fileSizeLimitBytes}
        onFileSizeLimitExceeded={onFileSizeLimitExceeded}
        onAddFiles={handleFileAddition}>
        {children}
      </IdleStateContent>
    );
  };

  if (disabled) {
    return <div className={`pointer-events-none h-full rounded ${className}`}>{renderContent()}</div>;
  }

  return (
    <Dropzone
      onDragStateChange={onDragStateChange}
      onFilesDrop={handleFileAddition}
      acceptedFileTypes={acceptedFileTypes}
      onFileTypeError={onFileTypeError}
      fileSizeLimitBytes={fileSizeLimitBytes}
      onFileSizeLimitExceeded={onFileSizeLimitExceeded}
      className={`h-full rounded ${className}`}>
      {renderContent()}
    </Dropzone>
  );
};
